// Depth fusion: every render's depth map votes on one grid of small cubes, and one surface is taken from
// it. A cube counts as solid only where the renders that see it agree; anything a render sees straight
// past is open space. So the model made from depth maps is one connected surface, with no stray sheets
// across doorways and no holes where two renders' meshes failed to meet. (A truncated signed distance
// field, as 3D scanners use, with the surface found by surface nets.)
//
// Depth here is metres from the camera, row 0 = the bottom of the image, as everywhere in depth.ts.

/** One render for fusion: [depth (metres, rows bottom first), w, h, camera x, y, z, yaw]. */
export type FuseView = [Float32Array, number, number, number, number, number, number];

export interface FuseOptions {
  /** cube size in metres (made larger if the scene needs more than maxCells) */
  voxel?: number;
  maxCells?: number;
  /** renders further than this from a cube don't vote on it */
  reach?: number;
  onProgress?: (f: number) => void;
}

const nextFrame = () => new Promise<void>((r) => setTimeout(r, 0));

/** Fuses the depth maps into one surface: positions (x, y, z) and triangle indices, faces pointing into open space. */
export async function fuseDepth(views: FuseView[], flip: boolean, o: FuseOptions = {}): Promise<{ pos: Float32Array; idx: Uint32Array; voxel: number }> {
  const reach = o.reach ?? 12;
  // the furthest each render measured: past it (CZDepth's limit, or sky) nothing is known
  const limits = views.map((v) => { let m = 0; for (let i = 0; i < v[0].length; i += 5) { const d = v[0][i]; if (d < 149 && d > m) m = d; } return m; });

  // where the surfaces are: a sample of every render's points, without the far few percent
  const xs: number[] = [], ys: number[] = [], zs: number[] = [];
  views.forEach((v, vi) => {
    const [D, w, h, cx, cy, cz, yaw] = v;
    for (let j = 0; j < h; j += 4) for (let i = 0; i < w; i += 4) {
      const d = D[j * w + i];
      if (!(d > 0.05) || d >= limits[vi] * 0.97) continue;
      let u = (i + 0.5) / w; if (flip) u = 1 - u;
      const lon = (u - 0.5) * 2 * Math.PI + yaw, lat = ((j + 0.5) / h - 0.5) * Math.PI;
      xs.push(cx + Math.cos(lat) * Math.sin(lon) * d); ys.push(cy + Math.sin(lat) * d); zs.push(cz - Math.cos(lat) * Math.cos(lon) * d);
    }
  });
  if (xs.length < 100) return { pos: new Float32Array(0), idx: new Uint32Array(0), voxel: 0 };
  const pct = (a: number[], p: number) => { const s = Float64Array.from(a).sort(); return s[Math.min(s.length - 1, Math.floor(p * s.length))]; };
  const pad = 0.25;
  const x0 = pct(xs, 0.002) - pad, x1 = pct(xs, 0.998) + pad;
  const y0 = pct(ys, 0.002) - pad, y1 = pct(ys, 0.998) + pad;
  const z0 = pct(zs, 0.002) - pad, z1 = pct(zs, 0.998) + pad;
  const vol = (x1 - x0) * (y1 - y0) * (z1 - z0);
  const V = Math.max(o.voxel ?? 0.06, Math.cbrt(vol / (o.maxCells ?? 6e6)));
  const nx = Math.ceil((x1 - x0) / V) + 1, ny = Math.ceil((y1 - y0) / V) + 1, nz = Math.ceil((z1 - z0) / V) + 1;
  const N = nx * ny * nz;
  // truncation: how far either side of a surface a render's vote reaches
  const mu = V * 2.5;
  const S = new Float32Array(N), W = new Float32Array(N);
  const at = (x: number, y: number, z: number) => (z * ny + y) * nx + x;

  // each render votes on the cubes around it
  const TAU = 2 * Math.PI;
  let done = 0;
  const total = views.length * nz;
  for (let vi = 0; vi < views.length; vi++) {
    const [D, w, h, cx, cy, cz, yaw] = views[vi];
    const lim = limits[vi];
    const ix0 = Math.max(0, Math.floor((cx - reach - x0) / V)), ix1 = Math.min(nx - 1, Math.ceil((cx + reach - x0) / V));
    const iy0 = Math.max(0, Math.floor((cy - reach - y0) / V)), iy1 = Math.min(ny - 1, Math.ceil((cy + reach - y0) / V));
    const iz0 = Math.max(0, Math.floor((cz - reach - z0) / V)), iz1 = Math.min(nz - 1, Math.ceil((cz + reach - z0) / V));
    for (let iz = 0; iz < nz; iz++) {
      if (iz >= iz0 && iz <= iz1) {
        const pz = z0 + iz * V - cz;
        for (let iy = iy0; iy <= iy1; iy++) {
          const py = y0 + iy * V - cy;
          for (let ix = ix0; ix <= ix1; ix++) {
            const px = x0 + ix * V - cx;
            const d = Math.sqrt(px * px + py * py + pz * pz);
            if (d > reach || d < 0.15) continue;
            let u = ((Math.atan2(px, -pz) - yaw) / TAU + 0.5) % 1; if (u < 0) u += 1;
            if (flip) u = 1 - u;
            const v = Math.asin(py / d) / Math.PI + 0.5;
            const sx = Math.min(w - 1, (u * w) | 0), sy = Math.min(h - 1, (v * h) | 0);
            let dm = D[sy * w + sx];
            // nothing measured that way (past the limit, or sky): open as far as the limit, unknown beyond
            if (!(dm > 0.02) || dm >= lim * 0.97) { if (d > lim * 0.95) continue; dm = lim * 0.97 + mu * 4; }
            const sdf = dm - d;
            if (sdf < -mu) continue; // behind what this render saw: it can't tell
            const t = sdf >= mu ? 1 : sdf / mu;
            // nearer renders measure finer; votes for open space count a little less than surfaces
            const wt = (sdf >= mu ? 0.6 : 1) / (1 + 0.12 * d);
            const k = at(ix, iy, iz);
            S[k] += t * wt; W[k] += wt;
          }
        }
      }
      if (++done % 32 === 0) { o.onProgress?.(0.85 * done / total); await nextFrame(); }
    }
  }

  // surface nets: a vertex in every cell the surface passes through, a quad across every edge it crosses
  const WMIN = 0.35;
  const f = (k: number) => (W[k] >= WMIN ? S[k] / W[k] : NaN);
  const cellVert = new Int32Array((nx - 1) * (ny - 1) * (nz - 1)).fill(-1);
  const cat = (x: number, y: number, z: number) => (z * (ny - 1) + y) * (nx - 1) + x;
  const pos: number[] = [];
  const cv = new Float64Array(8);
  for (let z = 0; z < nz - 1; z++) {
    for (let y = 0; y < ny - 1; y++) {
      for (let x = 0; x < nx - 1; x++) {
        let inside = 0, ok = true;
        for (let c = 0; c < 8; c++) {
          const val = f(at(x + (c & 1), y + ((c >> 1) & 1), z + ((c >> 2) & 1)));
          if (Number.isNaN(val)) { ok = false; break; }
          cv[c] = val; if (val < 0) inside |= 1 << c;
        }
        if (!ok || inside === 0 || inside === 255) continue;
        // average of where the surface crosses the cell's 12 edges
        let sx = 0, sy = 0, sz = 0, n = 0;
        for (let a = 0; a < 8; a++) for (const b of [a | 1, a | 2, a | 4]) {
          if (b === a) continue;
          const va = cv[a], vb = cv[b];
          if ((va < 0) === (vb < 0)) continue;
          const t = va / (va - vb);
          const ax = a & 1, ay = (a >> 1) & 1, az = (a >> 2) & 1, bx = b & 1, by = (b >> 1) & 1, bz = (b >> 2) & 1;
          sx += ax + (bx - ax) * t; sy += ay + (by - ay) * t; sz += az + (bz - az) * t; n++;
        }
        cellVert[cat(x, y, z)] = pos.length / 3;
        pos.push(x0 + (x + sx / n) * V, y0 + (y + sy / n) * V, z0 + (z + sz / n) * V);
      }
    }
    if (z % 16 === 0) { o.onProgress?.(0.85 + 0.1 * z / nz); await nextFrame(); }
  }
  const idx: number[] = [];
  // a quad of the four cells around an edge; `out` = the open side is the edge's far end (+axis)
  const quad = (a: number, b: number, c: number, d: number, out: boolean) => {
    if (a < 0 || b < 0 || c < 0 || d < 0) return;
    if (out) idx.push(a, b, c, a, c, d); else idx.push(a, c, b, a, d, c);
  };
  for (let z = 1; z < nz - 1; z++) for (let y = 1; y < ny - 1; y++) for (let x = 1; x < nx - 1; x++) {
    const v0 = f(at(x, y, z));
    if (Number.isNaN(v0)) continue;
    const in0 = v0 < 0;
    // edge along x to (x+1, y, z): cells (x, y-1|y, z-1|z), turning +y then +z gives a +x face
    if (x < nx - 2) { const v1 = f(at(x + 1, y, z)); if (!Number.isNaN(v1) && (v1 < 0) !== in0)
      quad(cellVert[cat(x, y - 1, z - 1)], cellVert[cat(x, y, z - 1)], cellVert[cat(x, y, z)], cellVert[cat(x, y - 1, z)], in0); }
    // edge along y: cells (x-1|x, y, z-1|z), turning +z then +x gives a +y face
    if (y < ny - 2) { const v1 = f(at(x, y + 1, z)); if (!Number.isNaN(v1) && (v1 < 0) !== in0)
      quad(cellVert[cat(x - 1, y, z - 1)], cellVert[cat(x - 1, y, z)], cellVert[cat(x, y, z)], cellVert[cat(x, y, z - 1)], in0); }
    // edge along z: cells (x-1|x, y-1|y, z), turning +x then +y gives a +z face
    if (z < nz - 2) { const v1 = f(at(x, y, z + 1)); if (!Number.isNaN(v1) && (v1 < 0) !== in0)
      quad(cellVert[cat(x - 1, y - 1, z)], cellVert[cat(x, y - 1, z)], cellVert[cat(x, y, z)], cellVert[cat(x - 1, y, z)], in0); }
  }
  o.onProgress?.(1);
  return { pos: new Float32Array(pos), idx: new Uint32Array(idx), voxel: V };
}
